import { createFileRoute } from '@tanstack/react-router';
import { DashboardPage, NoDashboardAccess } from '@/pages/dashboard';
import { LandingPage } from '@/pages/landing/LandingPage';
import { welcomeSeenKey } from '@/constants';
import { useState } from 'react';
import { createCaslRedirectGuard } from '@/lib/auth/permissionGuards';
import { useProfile } from '@/api/queries/useProfile';
import { usePermissions } from '@/hooks/usePermissions';

export const Route = createFileRoute('/$slug/')({
  beforeLoad: createCaslRedirectGuard('Dashboard', [
    { subject: 'RecordingDashboard', to: '/$slug/recording-dashboard' },
    { subject: 'OrgSettings', to: '/$slug/organization-settings/settings' }
  ]),
  component: DashboardOrWelcome
});

function DashboardOrWelcome() {
  const { slug } = Route.useParams();
  // Match context is frozen at match time — post-login it would still hold
  // user: undefined and produce a junk welcomeSeenKey. Read the user
  // reactively from the profile query instead (the route guard already
  // awaited it, so this resolves synchronously from cache).
  const { data: user } = useProfile();
  const { permissions } = usePermissions();

  if (!user) {
    return null;
  }

  // The guard already redirected members with a RecordingDashboard/OrgSettings
  // fallback, so reaching here without Dashboard access means no reachable
  // landing (external members without an elevated role). Show an honest
  // no-access surface instead of falling through to DashboardPage's 403.
  if (!permissions?.Dashboard?.read) {
    return <NoDashboardAccess />;
  }

  // Keyed so a user change can never carry the previous user's mount state.
  return <WelcomeGate key={`${slug}:${user._id}`} slug={slug} userId={user._id} />;
}

function WelcomeGate({ slug, userId }: { slug: string; userId: string }) {
  const [showDashboard, setShowDashboard] = useState(
    () => localStorage.getItem(welcomeSeenKey(slug, userId)) === 'true'
  );

  if (!showDashboard) {
    return (
      <LandingPage
        onContinue={() => {
          localStorage.setItem(welcomeSeenKey(slug, userId), 'true');
          setShowDashboard(true);
        }}
      />
    );
  }

  return <DashboardPage />;
}
